import type { ReactNode } from 'react';
import { useContext, createContext, useMemo, useRef, useState, useCallback } from 'react';
import type { DataTableId } from '../utils';
import type { DataTableBaseColumn, DataTableRow } from '../TableTypes';
import type { UserPreferencesTableColumnSettings } from '@server/user-preferences';
import { useSelector } from 'react-redux';
import { selectUserTableColumnSettings } from '../../../../reducers/userInfoReducer';
import { useInitializeMeasureColumnWidth } from './useInitializeMeasureColumnWidth';
import { useManipulateColumns } from './useManipulateColumns';

export interface CustomColumnsTableContextType {
  tableId: DataTableId;
  columns: DataTableBaseColumn[];
  rows: DataTableRow[];
  filteredAndSortedColumns?: DataTableBaseColumn[];
  tableColumnSettings?: UserPreferencesTableColumnSettings[];
  onColumnResizeEndCallback?: () => void;
  onColumnResize?: ({ columnId, width }: { columnId: string; width: number }) => void;
  resizingColumnId?: string;
  columnWidths?: Record<string, number>;
  setResizingColumnId?: (columnId?: string) => void;
  setColumnWidths?: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  overResizeHandleColumnId?: string | null;
  setOverResizeHandleColumnId?: (columnId: string | null) => void;
}

const CustomColumnsTableContext = createContext<CustomColumnsTableContextType>({
  tableId: '' as DataTableId,
  columns: [],
  rows: [],
});

export const useCustomColumnsTableContext = () => {
  const context = useContext(CustomColumnsTableContext);
  if (!context) {
    throw new Error(
      'useCustomColumnsTableContext must be used within a CustomColumnsTableContextProvider',
    );
  }
  return context;
};

// it's a workaround to not push for CustomColumnsTableContextProvider for all DataTables
// returns default values if not within the context
export const useCustomColumnsTableData = () => {
  const context = useContext(CustomColumnsTableContext);
  return {
    columnWidths: context?.columnWidths || {},
    resizingColumnId: context?.resizingColumnId,
    hasContext: !!context,
  };
};

export const CustomColumnsTableContextProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: CustomColumnsTableContextType;
}) => {
  const { onColumnResize } = useManipulateColumns(value.tableId);
  const [resizingColumnId, setResizingColumnId] = useState<string | undefined>(undefined);
  const [overResizeHandleColumnId, setOverResizeHandleColumnId] = useState<string | null>(null);
  const [initialWidthsMeasured, setInitialWidthsMeasured] = useState(false);

  const tableColumnSettings = useSelector(selectUserTableColumnSettings);

  const filteredAndSortedColumns = useMemo(() => {
    const currentTableSettings = tableColumnSettings?.find(
      (setting) => setting.tableId === value.tableId,
    );
    const excludedColumns = currentTableSettings?.excludedColumns ?? [];
    const filteredColumns = value.columns.filter((col) => !excludedColumns.includes(col.field));

    const columnsOrder = currentTableSettings?.order ?? {};
    const sortedColumns = filteredColumns.sort((a, b) => {
      // If the column is not in the order, it should be at the end
      const orderA = columnsOrder[a.field] ?? Number.MAX_SAFE_INTEGER;
      const orderB = columnsOrder[b.field] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    return sortedColumns;
  }, [value.columns, tableColumnSettings, value.tableId]);

  const _columnWidths = useMemo(() => {
    const currentTableSettings = tableColumnSettings?.find(
      (setting) => setting.tableId === value.tableId,
    );
    return currentTableSettings?.columnsWidth ?? {};
  }, [tableColumnSettings, value.tableId]);

  const columnWidthsRef = useRef<Record<string, number>>(_columnWidths);
  const [, setColumnWidths] = useState<Record<string, number>>(_columnWidths);

  useInitializeMeasureColumnWidth({
    filteredAndSortedColumns,
    rows: value.rows,
    columnWidthsRef,
    setColumnWidths,
    initialWidthsMeasured,
    setInitialWidthsMeasured,
    tableId: value.tableId,
  });

  const handleColumnResize = useCallback(
    ({ columnId, width }: { columnId: string; width: number }) => {
      // update the ref during resizing to be able to use it in onColumnResizeEnd callback -
      // without state there would be no real time visual update,
      // but state cannot be used in onColumnResizeEnd since it might not be up to date there once function is called
      if (columnWidthsRef) {
        columnWidthsRef.current = { ...columnWidthsRef?.current, [columnId]: width };
      }
      setColumnWidths?.((prev) => ({ ...prev, [columnId]: width }));
    },
    [setColumnWidths, columnWidthsRef],
  );

  const onColumnResizeEnd = useCallback(() => {
    setResizingColumnId?.(undefined);
    onColumnResize?.(columnWidthsRef?.current ?? {});
    setOverResizeHandleColumnId?.(null);
  }, [setResizingColumnId, columnWidthsRef, onColumnResize]);

  return (
    <CustomColumnsTableContext.Provider
      value={{
        ...value,
        filteredAndSortedColumns: filteredAndSortedColumns,
        tableColumnSettings,
        columnWidths: columnWidthsRef.current,
        resizingColumnId,
        setResizingColumnId,
        setColumnWidths,
        overResizeHandleColumnId,
        setOverResizeHandleColumnId,
        onColumnResize: handleColumnResize,
        onColumnResizeEndCallback: onColumnResizeEnd,
      }}
    >
      {children}
    </CustomColumnsTableContext.Provider>
  );
};
